import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const accountType = searchParams.get('type') || 'client';
  const isCoach = accountType === 'coach';
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      await API.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate(isCoach ? '/coach-login' : '/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F2F0E9] flex items-center justify-center p-4">
        <div className="bg-white border border-[#E7E5E4] p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-[#BC562B] mx-auto mb-6" />
          <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-4">
            Invalid Reset Link
          </h1>
          <p className="text-[#57534E] mb-6">
            This password reset link is invalid or has expired.
          </p>
          <Link
            to="/forgot-password"
            className="inline-block bg-[#2C5530] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1E3A21] transition-colors"
          >
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F0E9] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 mb-12 justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" 
            alt="Quiet Mind Golf" 
            className="h-12 w-auto"
          />
          <span className="font-['Playfair_Display'] text-xl font-medium text-[#1A1A1A]">
            Quiet Mind Golf
          </span>
        </Link>

        {success ? (
          <div className="bg-white border border-[#E7E5E4] p-8 text-center" data-testid="reset-success">
            <CheckCircle className="w-16 h-16 text-[#2C5530] mx-auto mb-6" />
            <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-4">
              Password Reset!
            </h1>
            <p className="text-[#57534E] mb-6">
              Your password has been successfully reset. You'll be redirected to login shortly.
            </p>
            <Link
              to="/login"
              className="inline-block bg-[#2C5530] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1E3A21] transition-colors"
            >
              Go to Login
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-[#E7E5E4] p-8">
            <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">
              Set New Password
            </h1>
            <p className="text-[#57534E] mb-6">
              Choose a strong password for your account.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 mb-6 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full pl-10 pr-12"
                    placeholder="Min 8 characters"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#1A1A1A]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full pl-10"
                    placeholder="Confirm your password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2C5530] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300 disabled:opacity-50"
                data-testid="reset-password-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
